import React, { useEffect, useMemo, useState } from 'react';
import useStepper from '@pages/sharedHooks/useStepper';
import SettingsIcon from '@mui/icons-material/Settings';
import VideoLabelIcon from '@mui/icons-material/VideoLabel';
import {
  useBulkMailboxDeactivationMutation,
  useBulkMailboxDeactivationValidateMutation,
} from '@services/WebApiService/MailboxApi';
import useLocalStorage from '@hooks/useLocalStorage';
import { constant } from '@config/BaseSetting';
import * as yup from 'yup';
import useForm from '@hooks/useForm';

const formSchema = yup.object().shape({
  supportTicket: yup.string().required('Support Ticket is required').default(''),
  escalationTicket: yup.string().default(''),
  description: yup.string().default(''),
  organization: yup.object().required('Organization is required').default(null),
  emailAccounts: yup.string().required('Email Accounts are required').default(''),
});

export default function useMailboxDeactivationModal({ onClose }) {
  const [accessTokenSet] = useLocalStorage(constant.ACCESS_TOKEN_STORAGE);
  const [showModal, setShowModal] = useState(false);
  const [validateResult, setValidateResult] = useState();
  const [formData, setFormData] = useState();
  const stepper = useStepper({});
  const form = useForm(formSchema);

  const [bulkMailboxDeactivationValidate, bulkMailboxDeactivationValidateState] =
    useBulkMailboxDeactivationValidateMutation();

  const [bulkMailboxDeactivation, bulkMailboxDeactivationState] =
    useBulkMailboxDeactivationMutation();

  useEffect(() => {
    if (bulkMailboxDeactivationValidateState.isSuccess) {
      setValidateResult(bulkMailboxDeactivationValidateState.data?.data);
      bulkMailboxDeactivationValidateState.reset();
      stepper.handleNext();
    }
  }, [bulkMailboxDeactivationValidateState.isSuccess]);

  const validateError = useMemo(() => {
    if (bulkMailboxDeactivationValidateState.error) {
      return (
        bulkMailboxDeactivationValidateState.error?.data?.message ||
        bulkMailboxDeactivationValidateState.error?.data?.['status-code'] ||
        bulkMailboxDeactivationValidateState.error?.status ||
        (bulkMailboxDeactivationValidateState.error.name === 'FetchError'
          ? 'Network error occurred'
          : 'Unknown error occurred')
      );
    }
    return null;
  }, [bulkMailboxDeactivationValidateState]);

  const handleValidate = (data) => {
    setFormData(data);
    bulkMailboxDeactivationValidate({
      accessToken: accessTokenSet,
      params: {
        'org-owner-id': data.organization.id,
        emails: data.emailAccounts?.split(',') ?? [],
      },
    });
  };

  useEffect(() => {
    if (bulkMailboxDeactivationState.isSuccess) {
      bulkMailboxDeactivationState.reset();
      setShowModal(false);
    }
  }, [bulkMailboxDeactivationState.isSuccess]);

  const submitError = useMemo(() => {
    if (bulkMailboxDeactivationState.error) {
      return (
        bulkMailboxDeactivationState.error?.data?.message ||
        bulkMailboxDeactivationState.error?.data?.['status-code'] ||
        bulkMailboxDeactivationState.error?.status ||
        (bulkMailboxDeactivationState.error.name === 'FetchError'
          ? 'Network error occurred'
          : 'Unknown error occurred')
      );
    }
    return null;
  }, [bulkMailboxDeactivationState]);

  const handleSubmit = () => {
    bulkMailboxDeactivation({
      accessToken: accessTokenSet,
      params: {
        'support-ticket': formData.supportTicket,
        'escalation-ticket': formData.escalationTicket,
        description: formData.description,
        payloads: validateResult
          .filter((m) => m.ableToRunBackup)
          .map((m) => ({
            'email-account-id': m.id,
            'org-owner-id': m.organizationOwnerId,
          })),
      },
    });
  };

  const handleBack = () => {
    setValidateResult(undefined);
    setFormData(undefined);
    bulkMailboxDeactivationValidateState.reset();
    stepper.handleBack();
  };

  const handleOpenModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);

  useEffect(() => {
    if (!showModal) {
      setValidateResult(undefined);
      setFormData(undefined);
      form.reset();
      bulkMailboxDeactivationValidateState.reset();
      stepper.handleReset();
      if (typeof onClose === 'function') onClose();
    }
  }, [showModal]);

  const steps = ['1', '2'];
  const stepIcons = {
    1: <SettingsIcon />,
    2: <VideoLabelIcon />,
  };

  return {
    showModal,
    handleOpenModal,
    handleCloseModal,
    steps,
    stepIcons,
    stepper,
    validateError,
    handleValidate,
    isValidating: bulkMailboxDeactivationValidateState.isLoading,
    validateResult,
    formData,
    submitError,
    handleSubmit,
    isSubmitting: bulkMailboxDeactivationState.isLoading,
    handleBack,
    form,
  };
}
